// src/components/Forum/PostsList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getPostsByCategory } from '../../services/forumApi';
import { getProviderForEdit } from '../../services/providersApi';
import { useLocations } from '../../utils/LocationContext';
import { useAuth } from '../../utils/AuthContext';
import { getJwtToken } from '../../utils/auth';
import { toast } from 'react-toastify';
import PostCard from './PostCard';
import CreatePostModal from './CreatePostModal';
import { MessageSquare, Plus } from 'lucide-react';
import './PostsList.css';

const PostsList = ({ categoryId, categoryName }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();
    const { selectedGlobalLocation } = useLocations();

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    //const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [corsError, setCorsError] = useState(null);

    // Es proveedor (de servicios o productos) si tiene providerRef/providerType en userData.
    // El JWT de Firebase no incluye un custom claim providerId, así que usamos userData
    // que ya viene resuelto desde el backend tras el login.
    const isProvider = !!(userData && (userData.providerRef || userData.providerType));
    // Solo proveedores ACTIVOS pueden contactar / ver el teléfono del autor.
    // null = todavía no resolvimos el estado.
    const [isActiveProvider, setIsActiveProvider] = useState(null);

    useEffect(() => {
        if (!isProvider || !userData?.providerRef) {
            setIsActiveProvider(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const prov = await getProviderForEdit(userData.providerRef);
                if (!cancelled) setIsActiveProvider(prov?.active === true);
            } catch (err) {
                console.warn('[PostsList] no se pudo verificar estado active del provider:', err);
                if (!cancelled) setIsActiveProvider(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isProvider, userData?.providerRef]);

    useEffect(() => {
        // El backend exige categoryId — no llamamos hasta tenerlo (puede
        // estar undefined mientras se resuelve categoryData en el padre).
        if (!categoryId) return;
        loadPosts(true);
        setCorsError(null);
    }, [categoryId, selectedGlobalLocation]);

    const loadPosts = async (reset = false) => {
        try {
            if (reset) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            // Determinar el serviceLocationId efectivo
            let serviceLocationId = null;
            if (selectedGlobalLocation) {
                serviceLocationId = selectedGlobalLocation.id !== undefined
                    ? selectedGlobalLocation.id
                    : selectedGlobalLocation.name;
            }

            const response = await getPostsByCategory(
                categoryId,
                serviceLocationId,
                20
            );

            // El backend devuelve {posts: [], total: number}
            const postsData = response.posts || [];
            const totalPosts = response.total || 0;

            if (reset) {
                setPosts(postsData);
            } else {
                setPosts(prev => [...prev, ...postsData]);
            }

            // Determinar si hay más posts basándose en el total
            setHasMore(postsData.length >= 20 && totalPosts > postsData.length);

        } catch (error) {
            if (error.isCorsError) {
                console.error('Error loading posts:', error);
                setCorsError(error);
                toast.error('Error de CORS: No se puede conectar con el servidor');
            } else if (error.response?.status === 404) {
                // "Sin posts" no es un error visible: dejamos el empty state
                // del componente y limpiamos la lista.
                if (reset) setPosts([]);
                setHasMore(false);
            } else {
                console.error('Error loading posts:', error);
                toast.error('Error al cargar los posts');
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleInterestAdded = (postId) => {
        setPosts(prev => prev.map(post =>
            post.id === postId
                ? { ...post, totalInterested: (post.totalInterested || 0) + 1 }
                : post
        ));
    };

    const handleCreatePost = async () => {
        // Verificar autenticación antes de abrir el modal
        const token = await getJwtToken();
        if (!token || !userData) {
            toast.info('Debes iniciar sesión para crear un post');
            navigate('/login', {
                state: {
                    from: { pathname: location.pathname, search: location.search },
                    intent: 'createPost'
                }
            });
            return;
        }

        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handlePostCreated = () => {
        // Recargar posts después de crear uno nuevo
        loadPosts(true);
    };

    const handleRetryConnection = () => {
        setCorsError(null);
        setLoading(true);
        loadPosts(true);
    };

    if (loading) {
        return (
            <>
                <div className="forum-posts-list-container">

                    <div className="forum-posts-grid">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="forum-posts-loading-shimmer"
                                style={{
                                    animationDelay: `${i * 0.1}s`
                                }}
                            />
                        ))}
                    </div>
                </div>

                <CreatePostModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    categoryId={categoryId}
                    categoryName={categoryName}
                    onPostCreated={handlePostCreated}
                />

                {/* Botón flotante para crear post */}
                <button
                    className="forum-floating-create-btn"
                    onClick={handleCreatePost}
                    title="Crear nuevo post"
                    style={{ display: isModalOpen ? 'none' : 'flex' }}
                >
                    <Plus size={16} />
                    <span>Postear</span>
                </button>
            </>
        );
    }

    return (
        <>
            <div className="forum-posts-list-container">

                {posts.length === 0 ? (
                    <div className="forum-posts-no-posts-container">
                        <MessageSquare size={48} />
                        <h3>No hay posts disponibles</h3>
                        <p>Sé el primero en crear un post en esta categoría y conecta con profesionales locales</p>
                    </div>
                ) : (
                    <>
                        <div className="forum-posts-grid">
                            {posts.map((post, index) => (
                                <div
                                    key={post.id}
                                    className="forum-posts-fade-in"
                                    style={{
                                        animationDelay: `${Math.min(index * 0.1, 0.6)}s`
                                    }}
                                >
                                    <PostCard
                                        post={post}
                                        isProvider={isProvider}
                                        isActiveProvider={isActiveProvider === true}
                                        onInterestAdded={handleInterestAdded}
                                    />
                                </div>
                            ))}
                        </div>

                        {loadingMore && (
                            <div className="forum-posts-loading-indicator">
                                <div className="forum-posts-loading-spinner"></div>
                                Cargando más posts...
                            </div>
                        )}

                        {hasMore && !loadingMore && (
                            <div className="forum-posts-load-more-container">
                                <button
                                    className="forum-posts-load-more-btn forum-posts-interactive-element"
                                    onClick={() => loadPosts(false)}
                                    disabled={loadingMore}
                                >
                                    Cargar más posts
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modal para crear posts */}
            <CreatePostModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                categoryId={categoryId}
                categoryName={categoryName}
                onPostCreated={handlePostCreated}
            />

            {/* Botón flotante para crear post */}
            <button
                className="forum-floating-create-btn"
                onClick={handleCreatePost}
                title="Crear nuevo post"
                style={{ display: isModalOpen ? 'none' : 'flex' }}
            >
                <Plus size={16} />
                <span>Postear</span>
            </button>
        </>
    );
};

export default PostsList;
